import { env } from "environment-interface/env";
import { useSandboxEditor } from "features/editor/Feature";
import { useNotifications } from "features/notifications/Feature";
import { useCsbApi } from "hooks/api/useCsbApi";
import { withBasePath } from "isomorphic/utils";
import type { IconType } from "prism-react";
import { Text, Stack, Button, Icon, Dialog, Box, Tooltip } from "prism-react";
import { useState } from "react";

import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { useAnalytics } from "hooks/useAnalytics";
import { useWorkspaceFeatureFlags } from "hooks/useWorkspaceFeatureFlags";
import { paths } from "utils/router";
import { executeVSCodeCommand } from "utils/vscode";

import { useGlobalModal } from "./useGlobalModal";

const Modal: React.FC<{ close: () => void }> = ({ close }) => {
  const csbApi = useCsbApi();
  const [creatingDevbox, setCreatingDevbox] = useState(false);
  const { id, title, description, customTemplate, collection } =
    useSandboxEditor();

  const { ubbBeta, friendOfCsb } = useWorkspaceFeatureFlags();
  const activeWorkspace = useActiveWorkspace();

  const track = useAnalytics();
  const [, notificationDispatch] = useNotifications();

  const submit = async () => {
    setCreatingDevbox(true);
    try {
      const MIGRATION_TEMPLATE_ID =
        "github/codesandbox/sandbox-templates/tree/main/sandbox-migration";

      const { data } = await csbApi.post<{
        data: {
          id: string;
          updatedAt: string;
          alias: string;
          team?: {
            id: string;
          };
        };
      }>({
        path: `/v1/sandboxes/fork/${MIGRATION_TEMPLATE_ID}`,
        data: {
          team_id: activeWorkspace?.id ?? null,
          v2: true,
          title,
          description,
          custom_template: customTemplate || undefined,
          privacy: 2, // private by default
          collection_id: collection?.id,
        },
      });

      // close current sandbox preview
      executeVSCodeCommand("codesandbox.layout.closeDevtool", {
        type: "UNASSIGNED_PORT",
        port: 0,
      });

      track("convert_to_devbox_submit");

      document.location.href = withBasePath(
        env,
        `/devbox/${data.id}?migrateFrom=${id}`,
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);

      setCreatingDevbox(false);

      notificationDispatch({
        type: "ADD_NOTIFICATION",
        notification: {
          type: "ALERT",
          message: "Error converting Sandbox to Devbox",
          showDismiss: true,
        },
      });
    }
  };

  if (!activeWorkspace) return;

  const percentageCreditUsage = Math.round(
    (activeWorkspace.usage.credits /
      (activeWorkspace.limits.includedCredits +
        (activeWorkspace.limits.onDemandCreditLimit ?? 0))) *
      100,
  );

  return (
    <Stack>
      <Stack
        css={{
          justifyContent: "space-between",
          textAlign: "center",
        }}
        horizontal
      >
        <Dialog.Title>
          <Stack horizontal>
            <Icon size={4} type="server" />
            <span>Are you sure you want to convert to a Devbox?</span>
          </Stack>
        </Dialog.Title>

        <Dialog.CloseIconButton onClick={close} />
      </Stack>

      <Stack gap={2}>
        <Dialog.Description css={{ paddingBottom: 0 }}>
          Converting to a Devbox make the code run on a Virtual Machine instead
          of the browser, which enables a more robust developer experience{" "}
          {ubbBeta && !friendOfCsb ? ` and charge VM credits for use.` : "."}
        </Dialog.Description>

        {ubbBeta && !friendOfCsb && (
          <Stack css={{ color: "$informative-info-base" }} horizontal>
            <Icon size={4} type="coins" />
            <Tooltip
              content={
                <Text>
                  {activeWorkspace.usage.credits} /{" "}
                  {activeWorkspace.limits.includedCredits +
                    (activeWorkspace.limits.onDemandCreditLimit ?? 0)}{" "}
                  credits
                </Text>
              }
            >
              <Text color="informative-info-base">
                You've used {percentageCreditUsage}% of your credits on the{" "}
                <a
                  href={paths.dashboard.workspaceVMSettings(activeWorkspace.id)}
                >
                  {activeWorkspace.name}
                </a>{" "}
                workspace.
              </Text>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      <Box
        css={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gridColumnGap: "$6",
          marginTop: "$6",
        }}
      >
        <Section iconType="vscode" title="Better developer tools">
          Access terminals and option to work in VS Code.
        </Section>
        <Section iconType="people" title="New ways of sharing">
          Devboxes default to private, collaborate with live sessions.
        </Section>
        <Section iconType="cloud" title="Control of Virtual Machine">
          By default, Devboxes will run on configurable VMs.
        </Section>
      </Box>

      <Stack css={{ justifyContent: "flex-end", marginTop: "$4" }} horizontal>
        <Stack
          css={{
            alignItems: "flex-start",
            color: "$informative-warning-base",
            textWrap: "balance",
            flex: 1,
          }}
          horizontal
        >
          <Icon size={4} type="circleBang" />

          <span>Converting to a Devbox will not delete this Sandbox</span>
        </Stack>
        <Dialog.Close asChild>
          <Button onClick={close} truncate={false}>
            Cancel
          </Button>
        </Dialog.Close>

        <Button
          css={{ padding: "0 $4" }}
          disabled={creatingDevbox}
          loading={creatingDevbox}
          onClick={submit}
          truncate={false}
          variant="primary"
        >
          Convert
        </Button>
      </Stack>
    </Stack>
  );
};

export const useConvertToDevboxModal = () =>
  useGlobalModal(Modal, { maxWidth: 650 });

const Section: React.FC<{
  children: React.ReactNode;
  iconType: IconType;
  title: string;
}> = ({ children, iconType, title }) => (
  <Stack gap={3}>
    <Box>
      <Icon size={5} type={iconType} />
    </Box>
    <Stack css={{ justifyContent: "space-between" }} gap={2}>
      <Text>{title}</Text>
      <Dialog.Description css={{ margin: 0 }}>{children}</Dialog.Description>
    </Stack>
  </Stack>
);
